/**
 * Role Permissions Utility
 * Centralized configuration for role-based access control
 */

// Define all user roles in the system
export const USER_ROLES = {
    SUPER_ADMIN: 'super_admin',
    TENANT_ADMIN: 'tenant_admin',
    USER: 'user'
  };
  
  // Define all route paths in the system
  export const ROUTE_PATHS = {
    // Productivity Tools — replace AI Coach in the nav. AI Coach route/component/service
    // are kept in the codebase but dormant (REQ-001 D1). Reversible.
    CORE_HYPOTHESIS: '/core-hypothesis',
    QUESTION_EVALUATOR: '/question-evaluator',
    REPORT_WRITER: '/report-writer',
    ARCHIVE: '/archive',
    USER_MANAGEMENT: '/user-management',
    FEEDBACK: '/feedback',
    TENANT_MANAGEMENT: '/tenant-management',
    CONTENT_MANAGEMENT: '/content-management'
  };
  
  // Define which roles can access which routes
  export const ROUTE_PERMISSIONS = {
    [ROUTE_PATHS.CORE_HYPOTHESIS]: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.TENANT_ADMIN,
      USER_ROLES.USER
    ],
    [ROUTE_PATHS.QUESTION_EVALUATOR]: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.TENANT_ADMIN,
      USER_ROLES.USER
    ],
    [ROUTE_PATHS.REPORT_WRITER]: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.TENANT_ADMIN,
      USER_ROLES.USER
    ],
    [ROUTE_PATHS.ARCHIVE]: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.TENANT_ADMIN,
      USER_ROLES.USER  // All roles have access (permissions enforced in component)
    ],
    [ROUTE_PATHS.USER_MANAGEMENT]: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.TENANT_ADMIN
    ],
    [ROUTE_PATHS.FEEDBACK]: [
      USER_ROLES.SUPER_ADMIN  // Only super_admin
    ],
    [ROUTE_PATHS.TENANT_MANAGEMENT]: [
      USER_ROLES.SUPER_ADMIN  // Only super_admin
    ],
    [ROUTE_PATHS.CONTENT_MANAGEMENT]: [
      USER_ROLES.SUPER_ADMIN  // Only super_admin
    ]
  };
  
  // Sidebar section groups (mock-up grouping): tools at top, then workspace,
  // then an Administration block. `label: null` renders a divider with no header.
  export const NAV_SECTIONS = {
    productivity: 'Productivity Tools',
    workspace: null,
    admin: 'Administration'
  };

  // Define navigation menu items with their metadata.
  // `group` keys into NAV_SECTIONS and drives the sidebar section labels/dividers.
  export const NAVIGATION_ITEMS = [
    {
      path: ROUTE_PATHS.CORE_HYPOTHESIS,
      label: 'Core Hypothesis',
      icon: 'MdLightbulb',
      group: 'productivity',
      allowedRoles: ROUTE_PERMISSIONS[ROUTE_PATHS.CORE_HYPOTHESIS]
    },
    {
      path: ROUTE_PATHS.QUESTION_EVALUATOR,
      label: 'Question Evaluator',
      icon: 'MdQuestionAnswer',
      group: 'productivity',
      allowedRoles: ROUTE_PERMISSIONS[ROUTE_PATHS.QUESTION_EVALUATOR]
    },
    {
      path: ROUTE_PATHS.REPORT_WRITER,
      label: 'Report Writer',
      icon: 'MdDescription',
      group: 'workspace',
      allowedRoles: ROUTE_PERMISSIONS[ROUTE_PATHS.REPORT_WRITER]
    },
    {
      path: ROUTE_PATHS.ARCHIVE,
      label: 'Archive',
      icon: 'MdArchive',
      group: 'workspace',
      allowedRoles: ROUTE_PERMISSIONS[ROUTE_PATHS.ARCHIVE]
    },
    {
      path: ROUTE_PATHS.FEEDBACK,
      label: 'Feedback',
      icon: 'MdFeedback',
      group: 'admin',
      allowedRoles: ROUTE_PERMISSIONS[ROUTE_PATHS.FEEDBACK]
    },
    {
      path: ROUTE_PATHS.USER_MANAGEMENT,
      label: 'User Management',
      icon: 'MdPeople',
      group: 'admin',
      allowedRoles: ROUTE_PERMISSIONS[ROUTE_PATHS.USER_MANAGEMENT]
    },
    {
      path: ROUTE_PATHS.TENANT_MANAGEMENT,
      label: 'Tenant Management',
      icon: 'MdBusiness',
      group: 'admin',
      allowedRoles: ROUTE_PERMISSIONS[ROUTE_PATHS.TENANT_MANAGEMENT]
    },
    {
      path: ROUTE_PATHS.CONTENT_MANAGEMENT,
      label: 'Content Management',
      icon: 'MdLibraryBooks',
      group: 'admin',
      allowedRoles: ROUTE_PERMISSIONS[ROUTE_PATHS.CONTENT_MANAGEMENT]
    }
  ];
  
  // Define default landing pages for each role
  export const DEFAULT_PATHS = {
    [USER_ROLES.SUPER_ADMIN]: ROUTE_PATHS.CORE_HYPOTHESIS,
    [USER_ROLES.TENANT_ADMIN]: ROUTE_PATHS.CORE_HYPOTHESIS,
    [USER_ROLES.USER]: ROUTE_PATHS.CORE_HYPOTHESIS
  };
  
  /**
   * Check if a user role has access to a specific route
   * @param {string} userRole - Current user's role
   * @param {string} routePath - Route path to check
   * @returns {boolean} - Whether the user has access
   */
  export const hasRouteAccess = (userRole, routePath) => {
    if (!userRole || !routePath) return false;
    
    const allowedRoles = ROUTE_PERMISSIONS[routePath] || [];
    return allowedRoles.includes(userRole);
  };
  
  /**
   * Get all accessible routes for a user role
   * @param {string} userRole - Current user's role
   * @returns {Array} - Array of accessible route paths
   */
  export const getAccessibleRoutes = (userRole) => {
    if (!userRole) return [];
    
    return Object.keys(ROUTE_PERMISSIONS).filter(route => 
      hasRouteAccess(userRole, route)
    );
  };
  
  /**
   * Get navigation items that a user role can access
   * @param {string} userRole - Current user's role
   * @returns {Array} - Array of navigation items the user can access
   */
  export const getAccessibleNavigationItems = (userRole) => {
    if (!userRole) return [];
    
    return NAVIGATION_ITEMS.filter(item => 
      item.allowedRoles.includes(userRole)
    );
  };
  
  /**
   * Get the default path for a user role
   * @param {string} userRole - Current user's role
   * @returns {string} - Default path for the role
   */
  export const getDefaultPathForRole = (userRole) => {
    return DEFAULT_PATHS[userRole] || ROUTE_PATHS.CORE_HYPOTHESIS;
  };
  
  /**
   * Check if a user role is an admin role (super_admin or tenant_admin)
   * @param {string} userRole - Current user's role
   * @returns {boolean} - Whether the user is an admin
   */
  export const isAdminRole = (userRole) => {
    return [USER_ROLES.SUPER_ADMIN, USER_ROLES.TENANT_ADMIN].includes(userRole);
  };
  
  /**
   * Check if a user role is super admin
   * @param {string} userRole - Current user's role
   * @returns {boolean} - Whether the user is super admin
   */
  export const isSuperAdmin = (userRole) => {
    return userRole === USER_ROLES.SUPER_ADMIN;
  };
  
  /**
   * Get a human-readable label for a user role
   * @param {string} userRole - Current user's role
   * @returns {string} - Human-readable role label
   */
  export const getRoleLabel = (userRole) => {
    const roleLabels = {
      [USER_ROLES.SUPER_ADMIN]: 'Super Admin',
      [USER_ROLES.TENANT_ADMIN]: 'Tenant Admin',
      [USER_ROLES.USER]: 'User'
    };
    
    return roleLabels[userRole] || 'Unknown Role';
  };
  
  /**
   * Validate if a role exists in the system
   * @param {string} role - Role to validate
   * @returns {boolean} - Whether the role is valid
   */
  export const isValidRole = (role) => {
    return Object.values(USER_ROLES).includes(role);
  };
  
  /**
   * Get all roles that can access a specific route
   * @param {string} routePath - Route path to check
   * @returns {Array} - Array of roles that can access the route
   */
  export const getRolesForRoute = (routePath) => {
    return ROUTE_PERMISSIONS[routePath] || [];
  };
  
  // Export route access summary for debugging/documentation
  export const getAccessMatrix = () => {
    const matrix = {};
    
    Object.values(USER_ROLES).forEach(role => {
      matrix[role] = {
        role: role,
        label: getRoleLabel(role),
        accessibleRoutes: getAccessibleRoutes(role),
        defaultPath: getDefaultPathForRole(role),
        isAdmin: isAdminRole(role),
        isSuperAdmin: isSuperAdmin(role)
      };
    });
    
    return matrix;
  };